package devxertech.com.seo

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.document
import org.w3c.dom.Element

private const val CANONICAL_ORIGIN = "https://devxertech.com"

private val routeMetadata = mapOf(
    "/blogs" to ("Software Development & AI Blog | DevxerTech" to "Read DevxerTech articles on software development, websites, mobile apps, AI assistants, chatbots and business automation."),
    "/blog-Page" to ("How AI Is Transforming Businesses in 2025 | DevxerTech" to "Explore how AI is changing business operations, customer experiences and everyday workflows in this article by Zahid Ramzan."),
    "/industry-edu" to ("Educational Software Development | DevxerTech" to "Explore DevxerTech's educational software development services, education technology solutions and tools for learners and educators."),
    "/fintech-Industry" to ("Financial Software Development | DevxerTech" to "Explore DevxerTech's financial software development services, fintech solutions and technology for financial business workflows."),
    "/healthcare-system" to ("Healthcare Software Development | DevxerTech" to "Explore DevxerTech's custom healthcare software development services and technology solutions for healthcare workflows."),
    "/sports-industry" to ("Sports Software Development | DevxerTech" to "Explore DevxerTech's sports software development services, sports management solutions and technology for coaching and training."),
    "/portfolio" to ("Software & App Development Portfolio | DevxerTech" to "Browse DevxerTech's project portfolio, including Aura Network, Safe Deposit, TCLD IOT, Varcity and Zone 360."),
    "/auraNetwork" to ("Aura Network Project | DevxerTech Portfolio" to "Explore the Aura Network project in DevxerTech's portfolio, including its mobile app and web views for Auroville's communal economy."),
    "/safeDeposit" to ("Safe Deposit App | DevxerTech Portfolio" to "Explore Safe Deposit in DevxerTech's portfolio, an escrow deposit payment app, with mobile app and web project views."),
    "/tcld" to ("TCLD IOT Project | DevxerTech Portfolio" to "Explore the TCLD IOT project in DevxerTech's portfolio, with API integration, AWS Amplify and ESPTouch Smart Configs."),
    "/varcity" to ("Varcity Trading Simulator | DevxerTech Portfolio" to "Explore Varcity in DevxerTech's portfolio, a trading simulator with real-time market data for traders at different experience levels."),
    "/zone" to ("Zone 360 Sports Coaching App | DevxerTech Portfolio" to "Explore Zone 360 in DevxerTech's portfolio, an AI-powered application designed to enhance soccer coaching skills."),
    "/contact-us" to ("Contact DevxerTech | Discuss Your Software Project" to "Contact DevxerTech to discuss your software project. Find our inquiry form, email, phone number and Lahore office details."),
    "/career-page" to ("Careers at DevxerTech | Explore Openings" to "Explore careers at DevxerTech, learn about our team and workplace, and browse the opportunities listed on our careers page."),
    "/Hr-Intern" to ("HR Intern Application | DevxerTech Careers" to "Read about the HR Intern role at DevxerTech and use the application form to share your details and resume.")
)

@Composable
fun PageMetadataForCurrentRoute() {
    val route = rememberPageContext().route.path
    val (title, description) = requireNotNull(routeMetadata[route]) { "Missing metadata for $route" }
    PageMetadata(title, description)
    if (route in setOf("/industry-edu", "/fintech-Industry", "/healthcare-system", "/sports-industry")) {
        ServiceJsonLd(route, title.substringBefore(" | "), description)
    }
}

/** A page-owned noindex tag is removed when leaving an unavailable article. */
@Composable
fun PageNoIndex(enabled: Boolean) {
    DisposableEffect(enabled) {
        val tag = if (enabled) document.createElement("meta").also {
            it.setAttribute("name", "robots")
            it.setAttribute("content", "noindex, follow")
            document.head?.appendChild(it)
        } else null
        onDispose { tag?.parentNode?.removeChild(tag) }
    }
}

enum class PageSeo(val title: String, val description: String) {
    Home(
        "Turning Ideas into Intelligent Solutions | DevxerTech",
        "DevxerTech builds custom AI chatbots, intelligent assistants and software solutions to help businesses support customers and streamline everyday work."
    ),
    Mobile(
        "Mobile App Development Services | DevxerTech",
        "Explore DevxerTech's custom mobile app development services, including Android, iOS and cross-platform compatibility, design and development."
    ),
    Web(
        "Website Design & Development Services | DevxerTech",
        "Discover DevxerTech's website design and development services, combining user-friendly design, functionality and a structured development workflow."
    ),
    Chatbot(
        "Custom AI Chatbot & Assistant Development | Devxer",
        "Custom AI chatbots and assistants for website support, lead qualification and business workflows. Explore our demo and discuss your project with Devxer."
    ),
    Ai(
        "AI Assistant & Automation Development Services | Devxer",
        "Build AI assistants for business knowledge, document search and workflow automation. Explore practical use cases and discuss your AI project with Devxer."
    ),
    Design(
        "UI/UX Design for Mobile & Web Apps | DevxerTech",
        "Explore DevxerTech's mobile and web app UI/UX design services, with responsive layouts, user-focused interfaces and attention to brand identity."
    ),
    DevOps(
        "DevOps & Software Delivery Services | DevxerTech",
        "Explore DevxerTech's DevOps services, including code inspection, integration, automated pipelines, testing and deployment from concept to production."
    )
}

private fun descriptionTags(): List<Element> {
    val nodes = document.head?.querySelectorAll("meta[name='description' i]") ?: return emptyList()
    return (0 until nodes.length).mapNotNull { nodes.item(it) as? Element }
}

private fun setMetadata(title: String, description: String?) {
    document.title = title
    val tags = descriptionTags()
    if (description == null) {
        tags.forEach { it.parentNode?.removeChild(it) }
        return
    }
    val tag = tags.firstOrNull() ?: document.createElement("meta").also {
        document.head?.appendChild(it)
    }
    tag.setAttribute("name", "description")
    tag.setAttribute("content", description)
    tags.drop(1).forEach { it.parentNode?.removeChild(it) }
}

private fun canonicalTags(): List<Element> {
    val nodes = document.head?.querySelectorAll("link[rel~='canonical' i]") ?: return emptyList()
    return (0 until nodes.length).mapNotNull { nodes.item(it) as? Element }
}

private fun setCanonical(routePath: String?) {
    val tags = canonicalTags()
    if (routePath == null) {
        tags.forEach { it.parentNode?.removeChild(it) }
        return
    }

    val canonicalPath = when (routePath) {
        "/homepage" -> "/"
        "/blog-Page" -> "/blogs/how-ai-is-transforming-businesses-in-2025"
        else -> routePath
    }
    val href = if (canonicalPath == "/") CANONICAL_ORIGIN else "$CANONICAL_ORIGIN$canonicalPath"
    val tag = tags.firstOrNull() ?: document.createElement("link").also {
        document.head?.appendChild(it)
    }
    tag.setAttribute("rel", "canonical")
    tag.setAttribute("href", href)
    tags.drop(1).forEach { it.parentNode?.removeChild(it) }
}

/** Keeps one absolute canonical URL in sync with the active Kobweb route. */
@Composable
fun PageCanonical(routePath: String) {
    DisposableEffect(routePath) {
        setCanonical(routePath)
        onDispose { setCanonical(null) }
    }
}

/** Adds the active route's canonical while composed inside a @Page scope. */
@Composable
fun PageCanonicalForCurrentRoute() {
    PageCanonical(rememberPageContext().route.path)
}

/** Page-scoped head updates work for export and router navigation without adding tags on recomposition. */
@Composable
fun PageMetadata(seo: PageSeo) = PageMetadata(seo.title, seo.description)

/** Applies content-specific metadata while keeping the active route canonical. */
@Composable
fun PageMetadata(title: String, description: String) {
    PageCanonicalForCurrentRoute()
    DisposableEffect(title, description) {
        val previousTitle = document.title
        val previousDescription = descriptionTags().firstOrNull()?.getAttribute("content")
        setMetadata(title, description)
        onDispose { setMetadata(previousTitle, previousDescription) }
    }
}
